import { ChangeEvent, ElementRef, FC, useEffect, useRef, useState } from 'react';
import { AddressSuggestions, DaDataSuggestion, DaDataAddress } from 'react-dadata';
import 'react-dadata/dist/react-dadata.css';
import './react-address-dadata.scss';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { setInfoOrderDetail } from 'redux/ordersDetails/slice';
import { formatOrderAddress } from 'utils/strActions';
import { ReactComponent as IconLocation } from './img/iconLocation.svg';

interface AddressDadata2Props {
  initialAddress?: {
    city?: string;
    street?: string;
    home?: string;
    apartment?: string;
  };
  placeholder?: string;
  error?: boolean;
}

export const AddressDadata2: FC<AddressDadata2Props> = ({
  initialAddress,
  placeholder = 'Укажи адрес',
  error = false,
}) => {
  const { infoOrderDetail } = useSelector((state: RootState) => state.ordersDetailsSlice);
  const [value, setValue] = useState<DaDataSuggestion<DaDataAddress> | null>(null);
  const [defaultValue, setDefaultValue] = useState<string | undefined>(undefined);
  const suggestionsRef = useRef<ElementRef<typeof AddressSuggestions>>(null);
  const dispatch = useDispatch();

  const handleChange = (suggestion?: DaDataSuggestion<DaDataAddress>) => {
    setValue(suggestion ?? null);
  };

  useEffect(() => {
    if (
      initialAddress &&
      (initialAddress.city || initialAddress.street || initialAddress.home)
    ) {
      const formattedAddress = formatOrderAddress({
        city: initialAddress.city,
        street: initialAddress.street,
        home: initialAddress.home,
        apartment: initialAddress.apartment,
      });

      if (suggestionsRef.current && formattedAddress) {
        suggestionsRef.current.setInputValue(formattedAddress);
      }
    }
  }, [
    initialAddress?.city,
    initialAddress?.street,
    initialAddress?.home,
    initialAddress?.apartment,
  ]);

  useEffect(() => {
    const suggestionData = value?.data;

    if (suggestionData) {
      suggestionData.flat !== null
        ? dispatch(setInfoOrderDetail({ key: 'apartment', value: suggestionData.flat }))
        : dispatch(setInfoOrderDetail({ key: 'apartment', value: '' }));
      suggestionData.city !== null
        ? dispatch(setInfoOrderDetail({ key: 'city', value: suggestionData.city }))
        : dispatch(setInfoOrderDetail({ key: 'city', value: '' }));
      suggestionData.street_with_type !== null
        ? dispatch(
            setInfoOrderDetail({ key: 'street', value: suggestionData.street_with_type }),
          )
        : dispatch(setInfoOrderDetail({ key: 'street', value: '' }));
      suggestionData.house !== null
        ? dispatch(setInfoOrderDetail({ key: 'home', value: suggestionData.house }))
        : dispatch(setInfoOrderDetail({ key: 'home', value: '' }));
    }

    if (defaultValue === '' && suggestionsRef.current) {
      suggestionsRef.current.setInputValue('');
      setValue(null);
      dispatch(setInfoOrderDetail({ key: 'apartment', value: '' }));
      dispatch(setInfoOrderDetail({ key: 'city', value: '' }));
      dispatch(setInfoOrderDetail({ key: 'street', value: '' }));
      dispatch(setInfoOrderDetail({ key: 'home', value: '' }));
    }
  }, [value, defaultValue]);

  return (
    <div
      style={{ position: 'relative' }}
      className={error ? 'address-dadata-error' : undefined}>
      <AddressSuggestions
        ref={suggestionsRef}
        token={`${process.env.REACT_APP_API_KEY}`}
        value={value ?? undefined}
        onChange={handleChange}
        defaultQuery={formatOrderAddress(infoOrderDetail)}
        selectOnBlur={true}
        count={5}
        highlightClassName='suggestion_highlight'
        currentSuggestionClassName='suggestion_select'
        inputProps={{
          placeholder,
          style: { paddingRight: '35px' },
          onChange: (e: ChangeEvent<HTMLInputElement>) => {
            if (e) setDefaultValue(e.target.value.trimStart());
          },
        }}
      />
      <IconLocation className='address-dadata-location-icon' />
    </div>
  );
};
